/**
 * LƯU / NẢI TIẾN TRÌNH — JSON có phiên bản + hàm migration.
 * Dùng localStorage (giới hạn ~10 MiB theo MDN, quá đủ cho save < 100 KB).
 * Khi save lớn hơn (nhiều màn, replay), chuyển sang IndexedDB/Dexie — API ở đây giữ nguyên.
 */
import { MAX_ROSTER, SAVE_VERSION, createMeta, type MetaState } from '../sim/meta';
import { CLASSES, CLASS_ORDER, type ClassId } from '../data/classes';
import { ensureProgression } from '../sim/craft';
import { MAX_EQUIPPED_SKILLS } from '../data/skills-extra';
import { FIRST_MAP_ID, difficultyById, type Difficulty } from '../data/maps';
import { sanitizeArenaProgress } from '../sim/arena-meta';

/**
 * KHOÁ LƯU TRỮ — CỐ Ý giữ nguyên tên `v3` dù save nay là v5.
 *
 * ⚠️ Đổi khoá = người chơi MẤT TIẾN TRÌNH (game đọc khoá mới, thấy rỗng, tạo save mới).
 * Phiên bản nằm trong PAYLOAD (`meta.version`) và `migrate()` lo phần nâng cấp, nên khoá
 * chỉ là cái tên ngăn kéo — đổi nó không mang lại lợi ích gì.
 */
const KEY = 'tdkungfu.save.v3';

export function saveMeta(meta: MetaState): void {
  try {
    meta.version = SAVE_VERSION;
    localStorage.setItem(KEY, JSON.stringify(meta));
  } catch (e) {
    console.warn('Không lưu được tiến trình', e);
  }
}

export function loadMeta(): MetaState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return createMeta();
    const parsed = JSON.parse(raw) as Partial<MetaState>;
    return migrateMeta(parsed);
  } catch (e) {
    console.warn('Save hỏng — tạo mới', e);
    return createMeta();
  }
}

export function resetSave(): void {
  localStorage.removeItem(KEY);
}

/**
 * Chống save cũ/hỏng làm vỡ game.
 *
 * NÂNG CẤP v3 → v4 (màn + độ khó + két đồ): save cũ KHÔNG có `clearedMaps`, `diamonds`,
 * `shards`, `craftedSkills`, `skillLoadout`, `currentMap`, `currentDiff`. Thay vì để UI
 * gặp `undefined`, hàm này vá đủ rồi gọi `ensureProgression()` (dựng 5 class × 7 ô chiêu
 * gốc) — người chơi cũ vào là chơi được ngay, không mất tướng/ vàng.
 *
 * v4 → v5 (tab Trang phục): thêm `formByClass`. Save cũ thiếu ⇒ `{}` = mọi class dùng dáng gốc;
 * id dáng lạ (ảnh đã bị xoá/ đổi tên) cũng tự lùi về dáng gốc lúc dựng hình, KHÔNG cần lọc ở đây.
 *
 * v5 → v6 (XOÁ HẲN PHỤ KIỆN + LUẬT SỔ 5): bỏ `ownedAccessories`/ `shards.acc`/ `loadout` của
 * từng tướng (save cũ còn thì bị bỏ qua), cắt sổ nhân vật về `MAX_ROSTER` = 5 và ĐỔI TÊN mọi
 * nhân vật thành TÊN CLASS — người chơi cũ đang thấy badge trang chủ 60 sẽ về đúng 5.
 *
 * v6 → v7 (XOÁ SỔ TƯỚNG): `heroes[]` được chuyển thành tiến trình CLASS rồi bị XOÁ khỏi state —
 * xem `classPower`/ `classShards` bên dưới.
 *
 * v7 → v8 (THÊM CHẾ ĐỘ ARENA): thêm MỘT trường `arena` (xem `ArenaProgress`). Save v7 KHÔNG có
 * trường này ⇒ `sanitizeArenaProgress(undefined, …)` trả về tiến trình Arena mặc định.
 *
 * ⚠️⚠️ ĐÂY LÀ ĐƯỜNG GÂY MẤT TIẾN TRÌNH NGUY HIỂM NHẤT CỦA CẢ DỰ ÁN — LUẬT BẤT DI BẤT DỊCH:
 * người dùng thật đang có save v7 với vàng/ class đã mở/ màn đã qua/ kim cương/ mảnh/ chiêu đã
 * ghép/ cấp tập luyện. Việc thêm Arena là BỔ SUNG, nên:
 *   · trường Arena thiếu/ rác ⇒ MẶC ĐỊNH, không ném;
 *   · MỌI trường TD cũ phải GIỮ NGUYÊN TỪNG CÁI MỘT (xem `tests/arena-meta.test.ts` — bài
 *     "save v7 đầy đủ ⇒ từng trường TD giữ nguyên", khoá bằng SỐ chứ không chỉ "load không lỗi");
 *   · KHÔNG đụng khoá localStorage (`KEY`) và KHÔNG đổi ngữ nghĩa trường nào đã có.
 */
/**
 * TẬP MỌI CLASS HỢP LỆ — dùng `Object.keys(CLASSES)` (7 class) chứ KHÔNG dùng `CLASS_ORDER` (5
 * class gốc).
 *
 * ⚠️ LỖI ĐÃ SỬA (đo được): dùng `CLASS_ORDER` thì `migrateMeta({shards:{hero:{carpenter:30}}})` cho
 * `classShards.carpenter === 0` ⇒ **mảnh của 2 class MỚI (Thợ mộc · Thợ lặn) bị NUỐT khi migrate**,
 * mà đó đúng là 2 class cần 30 mảnh để mở khoá ⇒ người chơi mất tiến trình. Trái nguyên tắc
 * "KHÔNG ĐƯỢC MẤT TIẾN TRÌNH" ở đầu hàm.
 */
const CLASSES_OF_CLASS_IDS = new Set<string>(Object.keys(CLASSES));

/**
 * CÙNG TẬP CLASS HỢP LỆ, DẠNG MẢNG — cho `sanitizeArenaProgress()` (nhận `readonly string[]`).
 *
 * Vì sao giữ CẢ HAI dạng thay vì đổi `sanitizeArenaProgress` sang nhận `Set`: `Set` là kiểu CỤ THỂ
 * của tầng save, còn mảng chỉ là "danh sách id hợp lệ" — truyền mảng giữ hàm đó THUẦN TUÝ và test
 * dựng được danh sách giả mà không phải import `data/classes.ts`. Hai biến suy từ CÙNG một
 * `Object.keys(CLASSES)` ⇒ không có bản sao nào để lệch (7 class, gồm cả `carpenter`/ `diver`).
 */
const CLASSES_OF_CLASS_IDS_ARRAY: readonly string[] = Object.keys(CLASSES);

/**
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * VÁ SỐ RÁC TỪ SAVE — BUG ĐÃ SẬP: `gold` (và 4 trường anh em) TỪNG LỌT QUA NGUYÊN XI
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ VÌ SAO CẦN HÀM NÀY (số ĐO THẬT, không phải phòng đoán):
 *
 * `migrateMeta()` dựng state bằng `{ ...base, ...raw, … }`. Với những trường KHÔNG được gán lại
 * tường minh, giá trị rác trong save đi thẳng vào state. Đo được TRƯỚC khi vá:
 *     migrateMeta({ version: 7, gold: null })       ⇒ gold = null
 *     migrateMeta({ version: 7, gold: 'abc' })      ⇒ gold = 'abc'
 *     migrateMeta({ version: 7, gold: NaN })        ⇒ gold = NaN
 *     migrateMeta({ version: 7, gold: undefined })  ⇒ gold = undefined
 *
 * ĐƯỜNG ĐI THẬT RẤT NGUY HIỂM: `loadMeta()` không chặn ⇒ `world.gold = this.meta.gold`
 * (`game.ts`) rồi `this.meta.gold += this.world.gold` ⇒ **`NaN` lan ra toàn bộ kinh tế** và người
 * chơi mất sạch tiến trình — mua gì cũng "không đủ vàng", nâng cấp nào cũng hỏng.
 *
 * ⚠️ VÌ SAO `typeof x === 'number'` KHÔNG ĐỦ (bẫy tinh vi): `typeof NaN === 'number'` và
 * `typeof Infinity === 'number'` đều ĐÚNG. Mấy vòng lặp dọn `training`/`classPower`/`classShards`
 * bên dưới từng chỉ kiểm `typeof … !== 'number'`, nên `NaN` ĐI QUA SẠCH. Đó là cùng một con bug,
 * chỉ khác chỗ đứng — nên phải dùng `Number.isFinite`, không dùng `typeof`.
 *
 * ⚠️ CHỈ SỐ NGUYÊN ≥ 0. Mọi trường áp hàm này là số ĐẾM (vàng · màn · mạng · wave · cấp): âm và
 * thập phân đều vô nghĩa và là dấu hiệu save hỏng. `classPower` CỐ Ý không dùng hàm này vì nó có
 * thể mang phần thập phân hợp lệ (`timePower` cũ chuyển sang, xem vòng lặp bên dưới).
 */
function intOr(value: unknown, fallback: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value as number)) : fallback;
}

export function migrateMeta(raw: Partial<MetaState>): MetaState {
  const base = createMeta();
  /*
   * `formByClass`: chỉ nhận khoá là class hợp lệ + giá trị chuỗi — save rác không làm hỏng UI.
   */
  const rawForms = (raw.formByClass ?? {}) as Record<string, unknown>;
  const formByClass: Partial<Record<ClassId, string>> = {};
  for (const c of CLASS_ORDER) {
    const v = rawForms[c];
    if (typeof v === 'string' && v) formByClass[c] = v;
  }
  /*
   * v6 → v7: SỔ TƯỚNG ĐÃ XOÁ (người dùng chốt *"Xoá sổ tướng đi tính năng đó giờ không dùng nữa"*).
   *
   * ⚠️ NGUYÊN TẮC MIGRATE: KHÔNG ĐƯỢC MẤT TIẾN TRÌNH người chơi đã trả tiền/ thời gian để có.
   * Save cũ có `heroes[]` (tối đa 5 bản ghi, mỗi bản ghi có `timePower`) ⇒ chuyển thành:
   *   · `unlocked`    ← mọi class xuất hiện trong sổ (họ đã chơi được class đó rồi)
   *   · `classPower`  ← CỘNG DỒN `timePower` của các bản ghi cùng class (đã trả vàng luyện thì giữ)
   *   · `classShards` ← quy đổi `shards.hero[class]` cũ (mảnh tướng) thành MẢNH CLASS cùng tên,
   *                     vì mảnh giờ dùng để mở khoá class / nâng cấp tướng chứ không để "ghép ra tướng"
   */
  const rawHeroes = Array.isArray((raw as { heroes?: unknown }).heroes)
    ? ((raw as { heroes: Array<Partial<{ id: string; classId: string; timePower: number }>> }).heroes)
    : [];
  const unlocked = new Set<ClassId>();
  const classPower: Partial<Record<ClassId, number>> = {};
  for (const h of rawHeroes) {
    const cid = h?.classId;
    if (typeof cid !== 'string' || !CLASSES_OF_CLASS_IDS.has(cid)) continue;
    const id = cid as ClassId;
    unlocked.add(id);
    const tp = Number.isFinite(h?.timePower) ? Math.max(0, Number(h.timePower)) : 0;
    if (tp > 0) classPower[id] = (classPower[id] ?? 0) + tp;
  }
  // class người chơi đã MỞ KHOÁ trong save mới (nếu có) cũng phải giữ
  if (Array.isArray((raw as { unlocked?: unknown }).unlocked)) {
    for (const c of (raw as { unlocked: unknown[] }).unlocked) {
      if (typeof c === 'string' && CLASSES_OF_CLASS_IDS.has(c)) unlocked.add(c as ClassId);
    }
  }
  // 5 CLASS GỐC LUÔN MỞ (như `createMeta`) — save cũ chưa có trường này cũng không bị khoá oan
  for (const c of CLASS_ORDER) unlocked.add(c);

  const meta: MetaState = {
    ...base,
    ...raw,
    version: SAVE_VERSION,
    training: { ...base.training, ...(raw.training ?? {}) },
    potions: { ...base.potions, ...(raw.potions ?? {}) },
    unlocked: [...unlocked],
    classPower: { ...base.classPower, ...classPower, ...((raw as { classPower?: Partial<Record<ClassId, number>> }).classPower ?? {}) },
    classShards: {
      // mảnh TƯỚNG cũ (shards.hero[class]) → mảnh CLASS cùng tên (không mất mảnh người chơi đã cày)
      ...Object.fromEntries(
        Object.entries((raw.shards?.hero as Record<string, number>) ?? {}).filter(([k]) => CLASSES_OF_CLASS_IDS.has(k)),
      ),
      ...((raw as { classShards?: Partial<Record<ClassId, number>> }).classShards ?? {}),
    },
    formByClass,
    /*
     * ---- VÁ SỐ RÁC (xem `intOr`) ----
     *
     * ⚠️ PHẢI ĐẶT SAU `...raw` để ghi ĐÈ giá trị thô vừa spread vào. Đặt trước là vô hiệu.
     * `base` là `createMeta()` — lấy mặc định từ ĐÓ, không hard-code lại (2 nguồn là 2 bản để lệch).
     *
     * `gold` là trường nguy hiểm nhất: xem chú thích `intOr` (NaN lan toàn bộ kinh tế).
     */
    gold: intOr(raw.gold, base.gold),
    stageIndex: intOr(raw.stageIndex, base.stageIndex),
    totalKills: intOr(raw.totalKills, base.totalKills),
    bestWave: intOr(raw.bestWave, base.bestWave),
    stagesCleared: intOr(raw.stagesCleared, base.stagesCleared),
    // ---- vá trường của v4 (save cũ sẽ thiếu)
    clearedMaps: Array.isArray(raw.clearedMaps) ? raw.clearedMaps.filter((k) => typeof k === 'string') : [],
    diamonds: intOr(raw.diamonds, base.diamonds),
    shards: {
      hero: { ...((raw.shards?.hero as Record<string, number>) ?? {}) },
      skill: { ...((raw.shards?.skill as Record<string, number>) ?? {}) },
    },
    craftedSkills: { ...(raw.craftedSkills ?? {}) },
    /*
     * ⚠️ CẮT NGAY VỀ TRẦN, không chỉ spread thô.
     *
     * Teammate `skill-cap-test` chỉ ra: chỉ `{...raw.skillLoadout}` thì save cũ (7 chiêu/ class) vẫn
     * nằm nguyên trong `meta` cho tới lần gọi hàm craft đầu tiên (`ensureProgression` mới cắt). Đường
     * đọc chuẩn đã đi qua `equippedSkills()` nên rủi ro thấp, NHƯNG để trạng thái trung gian "meta có
     * 7 chiêu" tồn tại là bom hẹn giờ — ai đọc thẳng `meta.skillLoadout` sẽ thấy vượt trần.
     * Cắt tại đây ⇒ sau `migrateMeta()` là `meta` ĐÃ hợp lệ, không phụ thuộc thứ tự gọi hàm.
     */
    skillLoadout: Object.fromEntries(
      Object.entries((raw.skillLoadout as Record<string, string[]>) ?? {}).map(([k, v]) => [
        k,
        Array.isArray(v) ? v.slice(0, MAX_EQUIPPED_SKILLS) : v,
      ]),
    ),
    currentMap: typeof raw.currentMap === 'string' ? raw.currentMap : FIRST_MAP_ID,
    // save cũ không có currentDiff; id lạ thì difficultyById() tự lùi về 'normal'
    currentDiff: difficultyById((raw.currentDiff ?? 'normal') as Difficulty).id,
    /*
     * v8: TIẾN TRÌNH ARENA — vá từ save cũ/ rác thành Arena MẶC ĐỊNH.
     *
     * ⚠️ Đặt SAU `...raw` (và gán lại tường minh) để `raw.arena` KHÔNG thể lọt thô vào state: save
     * rác có `arena: { mineLevels: 'x', unlockedHeroes: [null], wins: NaN }` nếu spread thẳng sẽ làm
     * HUD dựng nút từ id class không tồn tại ⇒ ném lỗi ngay lúc mở màn hình Arena.
     *
     * ⚠️ KHÔNG BAO GIỜ NÉM ở nhánh này: `sanitizeArenaProgress` nhận cả `undefined`/ `null`/ chuỗi/
     * mảng và LUÔN trả object hợp lệ (đường đi thật của save v7 hiện hữu). Một `throw` ở đây là
     * người chơi mất sạch tiến trình TD vì một trường của chế độ phụ.
     *
     * ⚠️ VÀ TUYỆT ĐỐI KHÔNG suy `arena.resource` từ `raw.gold` (hay ngược lại): tài nguyên Arena là
     * VÍ RIÊNG, xem `spendFromRunWallet()` trong `src/game.ts`. Trộn hai ví là phá cân bằng cả hai
     * chế độ, và đó là loại lỗi chỉ lộ ra sau khi người chơi đã chơi thật.
     */
    arena: sanitizeArenaProgress((raw as { arena?: unknown }).arena, CLASSES_OF_CLASS_IDS_ARRAY),
  };
  /*
   * ⚠️ XOÁ HẲN RÁC PHỤ KIỆN của save cũ. `...raw` ở trên mang theo `ownedAccessories`; nếu chỉ
   * "không dùng" mà không xoá thì mỗi lần `saveMeta()` nó lại được ghi tiếp vào save mới — rác
   * vĩnh viễn trong localStorage.
   */
  delete (meta as unknown as Record<string, unknown>).ownedAccessories;
  // ⚠️ Cùng lý do với phụ kiện: `...raw` mang theo `heroes` của save cũ ⇒ nếu không xoá thì mỗi
  // lần `saveMeta()` lại ghi sổ tướng cũ trở lại save mới (rác vĩnh viễn).
  delete (meta as unknown as Record<string, unknown>).heroes;

  // dựng lại 7 ô chiêu cho mọi class + lọc id lạ — LUẬT nằm ở sim/craft.ts, không chép lại
  ensureProgression(meta);
  /*
   * ---- BẢO ĐẢM MỌI CLASS ĐỀU CÓ SỐ HỢP LỆ ----
   *
   * ⚠️⚠️ `training` LÀ BUG THẬT ĐÃ ĐO ĐƯỢC — bản cũ chỉ kiểm `typeof x !== 'number'`, mà
   * `typeof NaN === 'number'` ⇒ `NaN` ĐI QUA SẠCH. `ensureProgression()` ngay trên KHÔNG đụng
   * `training` (nó chỉ dọn `diamonds` · `shards` · `classShards` · `classPower` — đã `grep` xác nhận),
   * nên `training.strength = NaN` vào thẳng state ⇒ cấp tập luyện thành NaN, tướng không luyện được
   * mà cũng không báo lỗi. Đã kiểm chứng: khôi phục guard cũ ⇒ bài test ĐỎ.
   *
   * ⚠️ `classShards`/`classPower`: ĐÂY LÀ PHÒNG THỦ THEO CHIỀU SÂU, KHÔNG PHẢI VÁ BUG ĐANG SỐNG.
   * Nói chính xác để người sau không hiểu sai: dòng cũ
   *     `meta.classShards[c] = meta.classShards[c] ?? 0;`
   * ĐÚNG LÀ vô hiệu với save rác (`'abc' ?? 0` ⇒ `'abc'`, vì `??` chỉ thay `null`/`undefined`).
   * NHƯNG nó KHÔNG BAO GIỜ CHẠY với giá trị rác: `ensureProgression()` phía trên đã gọi
   * `sanitizeClassMap()` cho cả hai trường (hàm đó dùng `Number.isFinite` + kẹp ≥ 0). Đã
   * mutation-test xác nhận: khôi phục nguyên dòng cũ ⇒ test vẫn XANH, đúng vì nó là code chết.
   * Giữ `intOr` ở đây cho NHẤT QUÁN và để không phụ thuộc thứ tự gọi hàm — nếu ai đảo
   * `ensureProgression` xuống dưới thì hai dòng này thành lá chắn thật.
   *
   * ⚠️ `classPower` CỐ Ý dùng `Number.isFinite` chứ KHÔNG `intOr`: nó có thể mang phần THẬP PHÂN hợp
   * lệ (chuyển từ `timePower` của sổ tướng cũ — vòng lặp `rawHeroes` phía trên cũng KHÔNG `Math.floor`).
   * Làm tròn nó là âm thầm cắt sức mạnh người chơi đã cày.
   */
  for (const c of CLASS_ORDER) {
    meta.training[c] = intOr(meta.training[c], 0);
    if (!Number.isFinite(meta.classPower[c])) meta.classPower[c] = 0;
  }
  // 2 class MỚI chưa mở: vẫn phải có chỗ đếm mảnh để Túi đồ hiển thị được
  for (const c of Object.keys(CLASSES) as ClassId[]) {
    meta.classShards[c] = intOr(meta.classShards[c], 0);
  }
  return meta;
}
